import { useEffect, useState } from "react";
import { PageSkeleton } from "@/components/PageSkeleton";
import api from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { PeriodFilter } from "@/components/PeriodFilter";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { UserCog, Coins, TrendingUp, ShoppingBag, Crown } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const th = "h-8 px-1.5 text-[11px] font-semibold whitespace-nowrap";
const td = "px-1.5 py-1.5 text-xs";

const statusBadge = (s) => {
  if (s === "Lunas") return "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300";
  if (s === "Sebagian") return "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300";
  return "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300";
};

const CHART_COLORS = ["#0A1633", "#C9A227", "#2563eb", "#059669", "#dc2626", "#7c3aed", "#0891b2", "#d97706"];

function GreetingLines({ o }) {
  if (!o.ucapan && !o.dari && !o.alamat) return null;
  return (
    <div className="mt-0.5 space-y-0.5 text-[10px] text-muted-foreground">
      {o.ucapan && <p className="whitespace-normal break-words"><span className="font-medium">Ucapan:</span> {o.ucapan}</p>}
      {o.dari && <p className="whitespace-normal break-words"><span className="font-medium">Dari:</span> {o.dari}</p>}
      {o.alamat && <p className="whitespace-normal break-words"><span className="font-medium">Alamat:</span> {o.alamat}</p>}
    </div>
  );
}

function CustBadge({ s }) {
  if (!s) return <span className="text-[10px] text-muted-foreground">-</span>;
  return <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${s === "Baru" ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300" : "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950 dark:text-sky-300"}`}>{s}</Badge>;
}

export default function AdminReports() {
  const [period, setPeriod, range, setRange] = usePersistentPeriod("admin-reports");
  const [data, setData] = useState(null);
  const [adminFilter, setAdminFilter] = useState("all");

  const qstr = () => period === "custom" ? `period=custom&start=${range.start}&end=${range.end}` : `period=${period}`;
  useEffect(() => {
    api.get(`/reports/by-admin?${qstr()}`).then((r) => setData(r.data));
    /* eslint-disable-next-line */
  }, [period, range.start, range.end]);

  if (!data) return <PageSkeleton stats={3} rows={4} chart />;
  const { admins, grand } = data;
  const bestId = admins.length ? admins[0].admin_id : null; // admins sorted by omzet desc
  const shown = adminFilter === "all" ? admins : admins.filter((a) => a.admin_id === adminFilter);
  const chartData = admins.map((a, i) => ({ name: a.admin_name.split(" ")[0], omzet: a.omzet, gross: a.gross, fill: CHART_COLORS[i % CHART_COLORS.length] }));

  return (
    <div className="space-y-4" data-testid="admin-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Laporan Penjualan per Admin</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Rincian order, omzet & laba kotor dipisah per admin — realtime dari data order/invoice.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={adminFilter} onValueChange={setAdminFilter}>
            <SelectTrigger data-testid="admin-filter" className="h-8 text-xs w-auto min-w-[140px]"><SelectValue placeholder="Semua Admin" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">Semua Admin</SelectItem>
              {admins.map((a) => <SelectItem key={a.admin_id} value={a.admin_id} className="text-xs">{a.admin_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="admin-grand-total">
        <Card data-testid="grand-omzet" className="p-4">
          <div className="flex items-center gap-2.5">
            <div className="grid place-items-center h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-950 shrink-0"><Coins className="h-4.5 w-4.5 text-emerald-600" style={{ width: 18, height: 18 }} /></div>
            <div className="min-w-0"><p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">Total Omzet</p><p className="font-heading text-base font-extrabold leading-tight truncate">{formatIDR(grand.omzet)}</p></div>
          </div>
        </Card>
        <Card data-testid="grand-gross" className="p-4">
          <div className="flex items-center gap-2.5">
            <div className="grid place-items-center h-9 w-9 rounded-lg bg-indigo-100 dark:bg-indigo-950 shrink-0"><TrendingUp className="h-4.5 w-4.5 text-indigo-600" style={{ width: 18, height: 18 }} /></div>
            <div className="min-w-0"><p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">Total Laba Kotor</p><p className="font-heading text-base font-extrabold leading-tight truncate text-indigo-600">{formatIDR(grand.gross)}</p></div>
          </div>
        </Card>
        <Card data-testid="grand-orders" className="p-4">
          <div className="flex items-center gap-2.5">
            <div className="grid place-items-center h-9 w-9 rounded-lg bg-sky-100 dark:bg-sky-950 shrink-0"><ShoppingBag className="h-4.5 w-4.5 text-sky-600" style={{ width: 18, height: 18 }} /></div>
            <div className="min-w-0"><p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">Total Order</p><p className="font-heading text-base font-extrabold leading-tight">{grand.order_count}</p></div>
          </div>
        </Card>
      </div>

      {/* Grafik perbandingan omzet antar admin */}
      {admins.length > 0 && (
        <Card className="p-4" data-testid="admin-bar-chart">
          <h3 className="font-heading text-sm font-semibold mb-1">Perbandingan Omzet & Laba Kotor antar Admin</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={52}
                tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(1)}jt` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}rb` : v} />
              <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", fontSize: 12 }} />
              <Bar dataKey="omzet" name="Omzet" radius={[6, 6, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
              <Bar dataKey="gross" name="Laba Kotor" radius={[6, 6, 0, 0]} fill="hsl(var(--muted-foreground))" opacity={0.45} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-1 justify-center text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#0A1633" }} /> Omzet</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-muted-foreground opacity-45" /> Laba Kotor</span>
          </div>
        </Card>
      )}

      {shown.length === 0 && (
        <Card className="p-10 text-center text-sm text-muted-foreground">Belum ada order pada periode ini.</Card>
      )}

      {shown.map((a) => (
        <Card key={a.admin_id} className="overflow-hidden" data-testid={`admin-group-${a.admin_id}`}>
          <div className="p-3 border-b border-border bg-secondary/40 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="grid place-items-center h-8 w-8 rounded-lg bg-primary/10 shrink-0"><UserCog className="h-4 w-4 text-primary" /></div>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-heading text-sm font-bold leading-tight" data-testid={`admin-name-${a.admin_id}`}>{a.admin_name}</p>
                  {a.admin_id === bestId && (
                    <Badge variant="outline" data-testid={`admin-best-${a.admin_id}`} className="gap-1 text-[10px] px-1.5 py-0 border-[#C9A227] bg-[#C9A227]/15 text-[#8a6d12] dark:text-[#e6c84a]">
                      <Crown className="h-3 w-3" /> Terbaik
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">{a.order_count} order</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              <span className="rounded-md bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 font-medium" data-testid={`admin-omzet-${a.admin_id}`}>Omzet: {formatIDR(a.omzet)}</span>
              <span className="rounded-md bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 px-2 py-0.5 font-medium">Modal: {formatIDR(a.modal)}</span>
              <span className="rounded-md bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 px-2 py-0.5 font-medium" data-testid={`admin-gross-${a.admin_id}`}>Laba Kotor: {formatIDR(a.gross)}</span>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={th}>No. Order</TableHead>
                  <TableHead className={th}>Tanggal</TableHead>
                  <TableHead className={th}>Pelanggan</TableHead>
                  <TableHead className={th}>Produk</TableHead>
                  <TableHead className={`${th} text-right`}>Jumlah</TableHead>
                  <TableHead className={`${th} text-right`}>Harga Jual</TableHead>
                  <TableHead className={`${th} text-right`}>Total Harga</TableHead>
                  <TableHead className={`${th} text-right`}>Harga Modal</TableHead>
                  <TableHead className={`${th} text-right`}>Laba Kotor</TableHead>
                  <TableHead className={th}>Bayar</TableHead>
                  <TableHead className={th}>Status Order</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {a.orders.map((o) => (
                  <TableRow key={o.id} data-testid={`admin-order-${o.id}`}>
                    <TableCell className={`${td} font-medium whitespace-nowrap`}>#{o.order_no}</TableCell>
                    <TableCell className={`${td} text-muted-foreground whitespace-nowrap`}>{formatDate(o.date)}</TableCell>
                    <TableCell className={td}>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">{o.customer_name}</span>
                        <CustBadge s={o.customer_status} />
                      </div>
                      {o.customer_phone && <p className="text-[10px] text-muted-foreground">{o.customer_phone}</p>}
                    </TableCell>
                    <TableCell className={`${td} max-w-[200px]`}>
                      <span className="font-medium">{o.category}</span>
                      <GreetingLines o={o} />
                    </TableCell>
                    <TableCell className={`${td} text-right`}>{o.quantity}</TableCell>
                    <TableCell className={`${td} text-right whitespace-nowrap`}>{formatIDR(o.unit_sell)}</TableCell>
                    <TableCell className={`${td} text-right whitespace-nowrap font-semibold`}>{formatIDR(o.amount)}</TableCell>
                    <TableCell className={`${td} text-right whitespace-nowrap text-muted-foreground`}>{formatIDR(o.base_price)}</TableCell>
                    <TableCell className={`${td} text-right whitespace-nowrap font-semibold text-indigo-600`}>{formatIDR(o.gross)}</TableCell>
                    <TableCell className={td}><Badge variant="outline" className={`${statusBadge(o.payment_status)} text-[10px] px-1.5 py-0`}>{o.payment_status}</Badge></TableCell>
                    <TableCell className={`${td} text-muted-foreground whitespace-nowrap`}>{o.delivery_status || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-border">
            {a.orders.map((o) => (
              <div key={o.id} data-testid={`admin-order-${o.id}`} className="p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">#{o.order_no} · {o.category}</p>
                    <p className="text-[11px] text-muted-foreground">{formatDate(o.date)}</p>
                  </div>
                  <p className="text-sm font-bold text-indigo-600 whitespace-nowrap">Laba {formatIDR(o.gross)}</p>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="font-medium">{o.customer_name}</span><CustBadge s={o.customer_status} />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                  <div className="flex justify-between"><span className="text-muted-foreground">Jumlah</span><span>{o.quantity}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Harga Jual</span><span>{formatIDR(o.unit_sell)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Total Harga</span><span className="font-semibold">{formatIDR(o.amount)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Harga Modal</span><span>{formatIDR(o.base_price)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Bayar</span><Badge variant="outline" className={`${statusBadge(o.payment_status)} text-[10px] px-1.5 py-0`}>{o.payment_status}</Badge></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Status Order</span><span>{o.delivery_status || "-"}</span></div>
                </div>
                <GreetingLines o={o} />
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
